import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { Dot } from "lucide-react";
import { makeStyles, createStyles, Theme, Box } from "@material-ui/core";
import clsx from "clsx";

// Définition des styles via Material-UI (v4)
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    otpInputContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1), // équivalent à gap-2
    },
    otpInputRoot: {
      "&:disabled": {
        cursor: "not-allowed",
      },
    },
    group: {
      display: "flex",
      alignItems: "center",
    },
    slot: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: 40,
      width: 40,
      fontSize: "0.875rem", // text-sm
      transition: "all 0.2s ease-in-out",
      borderTop: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:first-child": {
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
      },
      "&:last-child": {
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
      },
    },
    slotActive: {
      zIndex: 10,
      // Un outline pour simuler le focus
      outline: `2px solid ${theme.palette.primary.light}`,
      outlineOffset: -2,
    },
    caretContainer: {
      pointerEvents: "none",
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    caret: {
      width: 1,
      height: 16,
      backgroundColor: theme.palette.text.primary,
      animation: "$blink 1s infinite alternate",
    },
    "@keyframes blink": {
      from: { opacity: 1 },
      to: { opacity: 0 },
    },
    separator: {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
    },
  }),
);

// Déclaration modifiée pour autoriser null dans char
type SlotProps = {
  char?: string | null;
  hasFakeCaret?: boolean;
  isActive?: boolean;
};

// InputOTP : Wrappe le composant OTPInput de "input-otp"
const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => {
  const classes = useStyles();
  return (
    <OTPInput
      ref={ref}
      containerClassName={clsx(classes.otpInputContainer, containerClassName)}
      className={clsx(classes.otpInputRoot, className)}
      {...props}
    />
  );
});
InputOTP.displayName = "InputOTP";

// InputOTPGroup : conteneur pour les slots
const InputOTPGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const classes = useStyles();
  return (
    <div ref={ref} className={clsx(classes.group, className)} {...props} />
  );
});
InputOTPGroup.displayName = "InputOTPGroup";

// InputOTPSlot : affichage individuel d'une case OTP
const InputOTPSlot = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & SlotProps & { index: number }
>(({ index, className, char, hasFakeCaret, isActive, ...props }, ref) => {
  const classes = useStyles();
  const inputOTPContext = React.useContext(OTPInputContext);
  const slot = (inputOTPContext?.slots?.[index] || {}) as SlotProps;

  // On utilise "char" si fourni, sinon la valeur du slot du contexte ; sinon, chaîne vide
  const finalChar = char ?? slot.char ?? "";
  const finalHasFakeCaret = hasFakeCaret || slot.hasFakeCaret;
  const finalIsActive = isActive || slot.isActive;

  return (
    <div
      ref={ref}
      className={clsx(
        classes.slot,
        finalIsActive && classes.slotActive,
        className,
      )}
      {...props}
    >
      {finalChar}
      {finalHasFakeCaret && (
        <div className={classes.caretContainer}>
          <div className={classes.caret} />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

// InputOTPSeparator : affichage du séparateur (Dot)
const InputOTPSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const classes = useStyles();
  return (
    <div
      ref={ref}
      role="separator"
      className={clsx(classes.separator)}
      {...props}
    >
      <Dot />
    </div>
  );
});
InputOTPSeparator.displayName = "InputOTPSeparator";

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
